import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  LocationIcon,
  PeopleFillIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { ClubStackNavigatorParamList } from "../../../../../../../navigators/ClubNavigator";
import { useCancelDiningReservation } from "../../hooks/useCancelDiningReservation";
import type {
  DiningReservationSection_booking$data,
  DiningReservationSection_booking$key,
} from "./__generated__/DiningReservationSection_booking.graphql";
import { DetailRow } from "./components/DetailRow/DetailRow";
import { DiningAttendeesModal } from "./components/DiningAttendeesModal";
import { OtherPatronsRow } from "./components/OtherPatronsRow/OtherPatronsRow";
import { PlayerRow } from "./components/PlayerRow/PlayerRow";
import type { PlayerDisplay } from "./components/types";

type Props = {
  bookingRef: DiningReservationSection_booking$key | null;
};

export const DiningReservationSection = ({ bookingRef }: Props) => {
  const booking = useFragment(DiningReservationSectionFragment, bookingRef);
  const [isAttendeesModalOpen, setIsAttendeesModalOpen] = useState(false);
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const [cancelDiningReservation, isCancellingReservation] =
    useCancelDiningReservation();

  if (!booking) {
    return null;
  }

  const locationName =
    booking.roomName ??
    booking.startSlot?.agenda?.facility?.name ??
    t`Location to be determined`;

  const capacityLabel = getCapacityLabel(booking.partySize ?? null);

  const players = extractPlayers(booking);
  const ownerId =
    booking.ownerClient?.__typename === "Member"
      ? booking.ownerClient.id
      : null;

  const otherPatronsCount = Math.max(
    (booking.partySize ?? players.length) - players.length,
    0
  );

  const otherPatronsLabel =
    otherPatronsCount === 1 ? t`# other patron` : t`# other patrons`;

  return (
    <VStack gap={1} padding={5} backgroundColor="surface-primary">
      <VStack>
        <HStack
          justifyContent="space-between"
          alignItems="center"
          paddingBottom={5}
        >
          <Text variant="mainHeading">{t`Your Reservation`}</Text>
        </HStack>
        <VStack gap={1}>
          <DetailRow
            icon={<LocationIcon />}
            label={t`Location`}
            value={locationName}
          />
          <DetailRow
            icon={<PeopleFillIcon />}
            label={t`Party Size`}
            value={capacityLabel}
          />
        </VStack>
      </VStack>
      <Box height={1} backgroundColor="surface-dividers" />
      <VStack gap={1} marginBottom={booking.canUpdate ? 5 : 0}>
        {players.map((player, index) => (
          <PlayerRow
            key={player.id ?? `${player.displayName}-${index}`}
            player={player}
            isOwner={Boolean(ownerId && player.id === ownerId)}
          />
        ))}
        {otherPatronsCount > 0 ? (
          <OtherPatronsRow label={otherPatronsLabel} />
        ) : null}
      </VStack>
      {booking.canUpdate ? (
        <Button
          variant="outline"
          width="100%"
          accessibilityLabel={t`Edit booking`}
          onPress={() => setIsAttendeesModalOpen(true)}
        >
          {t`Edit Booking`}
        </Button>
      ) : null}
      {booking.status === "CONFIRMED" && booking.canUpdate ? (
        <>
          <Button
            variant="outline"
            width="100%"
            accessibilityLabel={t`Move Booking`}
            onPress={() => {
              navigation.navigate("MoveDiningBooking", {
                bookingId: booking.id,
                date: booking.startSlot?.agenda?.date ?? undefined,
              });
            }}
          >
            {t`Move Booking`}
          </Button>
          <Button
            variant="danger"
            width="100%"
            accessibilityLabel={t`Cancel Booking`}
            isLoading={isCancellingReservation}
            onPress={() => {
              cancelDiningReservation({
                input: {
                  bookingId: booking.id,
                },
              });
            }}
          >
            {t`Cancel Booking`}
          </Button>
        </>
      ) : null}
      {isAttendeesModalOpen ? (
        <DiningAttendeesModal
          isOpen={isAttendeesModalOpen}
          onClose={() => setIsAttendeesModalOpen(false)}
          bookingRef={booking}
        />
      ) : null}
    </VStack>
  );
};

const getCapacityLabel = (partySize: number | null) => {
  if (partySize != null) {
    return `${partySize} ${formatPeopleLabel(partySize)}`;
  }

  return t`Not specified`;
};

const formatPeopleLabel = (count: number) => {
  return count === 1 ? "Person" : "People";
};

const extractPlayers = (
  booking: DiningReservationSection_booking$data
): PlayerDisplay[] => {
  const players: PlayerDisplay[] = [];

  booking.rounds?.edges?.forEach((edge) => {
    const round = edge?.node;
    const player = round?.player;

    if (!round || !player) {
      return;
    }

    const displayName =
      player.fullName?.trim() ||
      [player.firstName, player.lastName]
        .filter((namePart): namePart is string => Boolean(namePart?.trim()))
        .join(" ")
        .trim();

    players.push({
      id: player.id ?? round.id ?? null,
      displayName: displayName || t`Guest`,
      firstName: player.firstName,
      lastName: player.lastName,
      profileImagePublicId: player.profileImage?.publicId ?? null,
    });
  });

  return players;
};

const DiningReservationSectionFragment = graphql`
  fragment DiningReservationSection_booking on Booking {
    ...DiningAttendeesModal_booking
    ... on DiningBooking {
      partySize
      roomName
      dietaryTags
      occasionTags
      notesFromMember
      status
    }
    id
    canUpdate
    ownerClient {
      __typename
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
    startSlot {
      id
      agenda {
        date
        facility {
          name
          minMaxGroupSize(bookingType: STANDARD) {
            min
            max
          }
        }
      }
    }
    rounds {
      edges {
        node {
          id
          player {
            id
            __typename
            fullName
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
  }
`;

export { DiningReservationSectionFragment };
